import { Component, OnInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CompanyJob } from '@edtech-ui/core/models/recruitment/get-all-job-listing';
import { RecruitmentJobService } from '@edtech-ui/core/services/recruitment-management/recruitment-job.service';
import { catchError, finalize, of, tap, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ToastrService } from 'ngx-toastr';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../../../shared/confirmation-dialog/confirmation-dialog.component';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
	selector: 'app-tab-jobs',
	standalone: true,
	imports: [CommonModule, FormsModule, MatProgressSpinnerModule, MatDialogModule, MatTooltipModule],
	templateUrl: './tab-jobs.component.html',
	styleUrls: ['./tab-jobs.component.scss']
})
export class TabJobsComponent implements OnInit {
	allPostJobs: CompanyJob[] = [];

	jobSearchQuery = '';
	selectedJobStatus = '';
	selectedJobType = '';
	selectedJobLocation = '';
	isGridView = true;

	// Custom Dropdown Open States
	isStatusDropdownOpen = false;
	isTypeDropdownOpen = false;
	isLocationDropdownOpen = false;
	isLoading = false;

	totalPage = 0;
	page = 1;
	limit = 8;
	total = 8;
	companyId = ''
	selectedVisibility: boolean | null = null;
	isVisibilityDropdownOpen = false;
	private searchSubject: Subject<string> = new Subject<string>();

	constructor(
		private router: Router,
		private jobService: RecruitmentJobService,
		private toastr: ToastrService,
		private dialog: MatDialog
	) { }

	ngOnInit(): void {
		this.companyId = localStorage.getItem('company_id') || '';

		// Load state from service
		this.page = this.jobService.lastJobsPage || 1;

		this.getAllJobs();

		this.searchSubject.pipe(
			debounceTime(400),
			distinctUntilChanged()
		).subscribe((search) => {
			this.jobSearchQuery = search;
			this.page = 1;
			this.savePage();
			this.getAllJobs();
		});
	}

	savePage(): void {
		this.jobService.lastJobsPage = this.page;
	}


	getAllJobs(): void {
		this.isLoading = true;

		let status = '';

		if (this.selectedJobStatus === 'Pending') {
			status = 'pending';
		} else if (this.selectedJobStatus === 'Draft') {
			status = 'draft';
		} else if (this.selectedJobStatus === 'Active') {
			status = 'active';
		} else if (this.selectedJobStatus === 'Closed') {
			status = 'closed';
		}

		const filters = {
			job_type: this.selectedJobType,
			work_mode: this.selectedJobLocation,
			is_visible: this.selectedVisibility,
		};

		this.jobService
			.getAllJobPosting(
				this.companyId,
				this.page,
				this.limit,
				status,
				this.jobSearchQuery,
				filters
			).pipe(tap(response => {
				if (response?.Success) {
					this.allPostJobs = response.Data.jobs;
					this.total = response.Data.total;
					this.totalPage = response.Data.total_pages;
					this.limit = response.Data.limit;
					this.page = response.Data.page;
				}
			}),
				finalize(() => {
					this.isLoading = false;
				}),
				catchError(() => {
					this.toastr.error('Something went wrong. Please try again');
					return of(null);
				})
			).subscribe();
	}


	capitalize(val: string): string {
		return val ? val.charAt(0).toUpperCase() + val.slice(1) : '';
	}


	toggleVisibilityDropdown(event: Event): void {
		event.stopPropagation();
		this.isVisibilityDropdownOpen = !this.isVisibilityDropdownOpen;
		this.isStatusDropdownOpen = false;
		this.isTypeDropdownOpen = false;
		this.isLocationDropdownOpen = false;
	}


	selectVisibility(value: boolean | null): void {
		this.selectedVisibility = value;
		this.isVisibilityDropdownOpen = false;
		this.page = 1;
		this.savePage();
		this.getAllJobs();
	}


	toggleStatusDropdown(event: Event): void {
		event.stopPropagation();
		this.isStatusDropdownOpen = !this.isStatusDropdownOpen;
		this.isTypeDropdownOpen = false;
		this.isLocationDropdownOpen = false;
	}

	toggleTypeDropdown(event: Event): void {
		event.stopPropagation();
		this.isTypeDropdownOpen = !this.isTypeDropdownOpen;
		this.isStatusDropdownOpen = false;
		this.isLocationDropdownOpen = false;
	}

	toggleLocationDropdown(event: Event): void {
		event.stopPropagation();
		this.isLocationDropdownOpen = !this.isLocationDropdownOpen;
		this.isStatusDropdownOpen = false;
		this.isTypeDropdownOpen = false;
	}

	selectStatus(status: string): void {
		this.selectedJobStatus = status;
		this.isStatusDropdownOpen = false;
		this.page = 1;
		this.savePage();
		this.getAllJobs();
	}

	selectType(type: string): void {
		this.selectedJobType = type;
		this.isTypeDropdownOpen = false;
		this.page = 1;
		this.savePage();
		this.getAllJobs();
	}


	selectLocation(location: string): void {
		this.selectedJobLocation = location;
		this.isLocationDropdownOpen = false;
		this.page = 1;
		this.savePage();
		this.getAllJobs();
	}

	@HostListener('document:click')
	closeAll(): void {
		this.isStatusDropdownOpen = false;
		this.isTypeDropdownOpen = false;
		this.isLocationDropdownOpen = false;
		this.isVisibilityDropdownOpen = false;
	}

	viewJobDetail(jobId: string): void {
		this.router.navigate([`/recruiter-dashboard/jobs/${jobId}`]);
	}

	navigateToCreateJob(): void {
		this.router.navigate(['/recruiter-dashboard/jobs/create']);
	}

	isJobDeletable(status?: string): boolean {
		if (!status) return false;
		return ['draft', 'pending', 'closed', 'rejected'].includes(status.toLowerCase());
	}

	deleteJob(jobId: string, event: Event): void {
		event.stopPropagation();

		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			width: '400px',
			disableClose: true,
			data: {
				title: 'Delete Job',
				message: 'Are you sure you want to delete this job? This action cannot be undone.',
				confirmText: 'Delete',
				cancelText: 'Cancel',
				type: 'quit'
			}
		});

		dialogRef.afterClosed().subscribe(result => {
			if (result) {
				this.jobService.deleteJob(jobId, this.companyId).pipe(
					tap(() => {
						this.toastr.success('Job deleted successfully');
						this.getAllJobs();
					}),
					catchError(() => {
						this.toastr.error('Failed to delete job');
						return of(null);
					})
				).subscribe();
			}
		});
	}


	clearSearch(): void {
		this.jobSearchQuery = '';
		this.page = 1;
		this.savePage();
		this.getAllJobs();
	}

	nextPage(): void {
		if (this.page < this.totalPage) {
			this.page++;
			this.savePage();
			this.getAllJobs();
		}
	}

	prevPage(): void {
		if (this.page > 1) {
			this.page--;
			this.savePage();
			this.getAllJobs();
		}
	}

	getMathMin(a: number, b: number): number {
		return Math.min(a, b);
	}


	clearAllFilters() {
		this.jobSearchQuery = '';
		this.selectedJobStatus = '';
		this.selectedJobType = '';
		this.selectedJobLocation = '';
		this.selectedVisibility = null;
		this.page = 1;
		this.savePage();
		this.getAllJobs();
	}

	onSearchChange(): void {
		this.savePage();
		this.searchSubject.next(this.jobSearchQuery.trim());
	}


	hasActiveFilters(): boolean {
		return !!(
			this.jobSearchQuery ||
			this.selectedJobStatus ||
			this.selectedJobType ||
			this.selectedJobLocation ||
			this.selectedVisibility !== null
		);
	}

	truncateText(text: string, limit: number): string {
		return text?.length > limit ? text.slice(0, limit) + '...' : text;
	}

}
